import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { CalendarIcon } from '../../components/icons/CalendarIcon'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useSelector } from 'react-redux'
import { AppSelector } from '../../store/app'
import { StyledIconButton } from '../../components/Button'
import {
  EyeIcon,
  HeartOutlinedIcon,
  ShieldIcon,
  WaterUtilityOutlinedIcon,
} from '../../components/icons'
import { LocationIcon } from '../../components/icons/LocationIcon'
import { NoteIcon } from '../../components/icons/NoteIcon'
import { WaterTestKitRequestsSelectors } from '../../store/waterTestKitRequests'
import { WaterTestKitResultsSelector } from '../../store/waterTestKitResults'
import { WaterTestParamsSelectors } from '../../store/waterTestParams'
import { WaterTestParameterResult } from '../../types'
import { formatDate } from '../../utils'
import { ParameterResult } from '../TestStrip/Results/subComponents'

export const ResultsPage = () => {
  const theme = useTheme()
  const summary = useSelector(WaterTestKitResultsSelector.makeLatestResult)
  const shortSummary = useSelector(WaterTestKitResultsSelector.makeLatestResultsSummaryShort)

  const isSharedView = useSelector(AppSelector.getIsSharedView) || !!localStorage.getItem('shareToken')
  const latestAddress = useSelector(WaterTestKitRequestsSelectors.makeLatestAddress)
  const latestCity = useSelector(WaterTestKitRequestsSelectors.makeLatestCity)
  const params = useSelector(WaterTestParamsSelectors.getWaterTestParams)
  const groupedParams = useSelector(WaterTestKitResultsSelector.makeLatestResultGroupedByParamGroup)

  const makeTotal = (shortSummary) => {
    return Object.values(shortSummary || {}).reduce((acc, curr: any) => {
      acc += curr.count || 0
      return acc
    }, 0)
  }
  const processDate =
    summary && summary.processedAt ? formatDate(summary.processedAt as string) : ''
  const getHighestSeverity = (results: WaterTestParameterResult[]) => {
    const severityMap = {
      severity: 'neutral',
      label: 'Neutral',
    }

    results.forEach((result) => {
      if (result.label === 'High' || result.label === 'Low') {
        severityMap.severity = 'error'
        severityMap.label = 'Action Needed'
      }
      if (result.label === 'Caution' && severityMap.severity !== 'error') {
        severityMap.severity = 'warning'
        severityMap.label = 'Monitor'
      }
      if (result.label === 'Good' && !['error', 'warning'].includes(severityMap.severity)) {
        severityMap.severity = 'success'
        severityMap.label = 'Maintain'
      }
    })

    return severityMap
  }

  const labelIcon = (label: string) => {

    if (label === 'Health & Safety') {
      return <StyledIconButton status="error" actionIcon={<HeartOutlinedIcon fontSize="small" />} buttonProps={{sx:{p:1}}} />
    }
    if (label === 'Disinfectants & Byproducts') {
      return <StyledIconButton status="warning" actionIcon={<ShieldIcon color='warning' fontSize="small"/>} buttonProps={{sx:{p:1}}}/>
    }
    if (label === 'Minerals & Metals (Scaling & Build-up)') {
      return <StyledIconButton status="secondary" actionIcon={<WaterUtilityOutlinedIcon color='primary' fontSize="small" />} buttonProps={{sx:{p:1}}} />
    }
    if (label === 'Infrastructure & Corrosion Control') {
      return <StyledIconButton status="error" actionIcon={<EyeIcon />} buttonProps={{sx:{p:1}}} />
    }
  }

  const getParameterColor = (parameterName: string) => {

    const parameter = params.find((param) => param.name === parameterName)
    return parameter?.colors?.[0] || '#E1E3E7' // Default color if not found
  }
  return (
    <Grid container spacing={2} direction="column" sx={{ width: '100%' }}>
      <Grid container direction="column" sx={{ width: '100%',py:2 }}>
        <Typography>Test Results</Typography>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          wrap="nowrap"
          sx={{ width: '100%' }}
        >
          <CalendarIcon color="primary" />
          <Typography variant="body1" color="secondary">
            Tested On {processDate}
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          wrap="nowrap"
          sx={{ width: '100%' }}
        >
          <LocationIcon color="primary" />
          <Typography variant="body1" color="secondary">
            {isSharedView ? latestCity : latestAddress}
          </Typography>
        </Grid>
        <Divider />
      </Grid>
      <Typography variant="body1">
        Comprehensive analysis of {params.length} water quality parameters
      </Typography>
      <Grid
        container
        spacing={2}
        direction="column"
        sx={{
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          borderRadius: theme.shape.borderRadius,
          p: 2,
        }}
      >
        <NoteIcon color="primary" />
        <Typography variant="h4">Test Summary</Typography>
        <Grid container direction="row" spacing={4}>
          <Chip
            variant="filled-neutral-light"
            size="medium"
            label={`Total Parameters: ${makeTotal(shortSummary)}`}
          />
          {shortSummary.Good && (
            <Chip
              variant="filled-success-light"
              size="medium"
              label={`Maintain: ${shortSummary.Good.count}`}
            />
          )}
          {shortSummary.Caution && (
            <Chip
              variant="filled-warning-light"
              size="medium"
              label={`Monitor: ${shortSummary.Caution.count}`}
            />
          )}
          {shortSummary.High && (
            <Chip
              variant="filled-error-light"
              size="medium"
              label={`Action needed: ${shortSummary.High.count}`}
            />
          )}
        </Grid>
      </Grid>
      <Typography variant="h4">Detailed Results</Typography>
      <Typography>Tap any parameter for detailed information</Typography>
      {groupedParams &&
        Object.keys(groupedParams).map((k) => {
          const { severity, label } = getHighestSeverity(groupedParams[k].items)
          return (
            <Grid
              key={k}
              container
              spacing={2}
              direction="column"
              sx={{
                border: '1px solid',
                borderColor: theme.palette.grey[300],
                borderRadius: (theme.shape.borderRadius as number) / 2,
                }}
            >
              <Accordion elevation={0} >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    {labelIcon(k)}
                    <Box>
                      <Stack
                        direction="row"
                        flexWrap="nowrap"
                        spacing={2}
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Typography>{k}</Typography>
                        <Chip
                          variant={`filled-${severity}-light` as any}
                          label={label}
                          size="small"
                        />
                      </Stack>
                      <Typography variant="body-small" color="secondary">
                        {groupedParams[k].items.length} Parameters
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1} direction='column'>
                  {groupedParams[k].items.map((item: WaterTestParameterResult, index: number) => (
                      <ParameterResult key={item.name || index} parameter={item} color={getParameterColor(item.name)}/>
                  ))}
                  </Stack>

                </AccordionDetails>
              </Accordion>
            </Grid>
          )
        })}
    </Grid>
  )
}
